import { useEffect, useRef } from "react";
import { useToast } from "@chakra-ui/react";
import { getBid } from "../../lib/Auth";
import {
  ivApi,
  isOk,
  watchedVerifications,
  watchVerification,
  finishVerification,
  finishedNotice,
  IV_FINISHED_EVENT,
} from "./insuranceVerification";

/**
 * InsuranceVerificationNotifier — the Inbox notification when a check finishes (SS-3944).
 *
 * Mounted once for office staff, outside the routes: it follows every running check the
 * practice's Insurance cards recorded (insuranceVerification.watchVerification), so a
 * breakdown that is ready hours later is announced whichever conversation or page is on
 * screen, and after a reload. A check is announced once. The notification stays until it
 * is closed, and is closed with the practice: it names a patient, so it never outlives a
 * switch to another practice or a logout.
 */

const POLL_MS = 20000;
// a round that has not finished by then (a request the session ended under never answers) is let go
const roundDeadline = (pollMs) => Math.max(3 * pollMs, 60000);

export default function InsuranceVerificationNotifier({ pollMs = POLL_MS }) {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const bid = getBid();
  const busy = useRef(false);
  // announced in this tab: never again, even when storage could not record it
  const announced = useRef(new Set());

  useEffect(() => {
    if (!bid) return undefined;
    let live = true;
    let round = 0;
    let stop;
    const stopped = new Promise((resolve) => { stop = resolve; });
    const shown = [];
    // every followed check is asked at once: one that does not answer never holds up the others
    const tick = (mine) => Promise.all(watchedVerifications(bid).map(async (entry) => {
      const r = await ivApi.getVerification(entry.id);
      // unmounted, or this round is over (done, past its deadline, or taken over)
      if (!live || mine !== round) return;
      if (!isOk(r) || !r.data) return; // not answered now: asked again on the next round
      const v = r.data;
      if (v.status === "done" || v.status === "failed") {
        // announced once, whichever tab or poller saw it first
        if (announced.current.has(entry.id) || !finishVerification(bid, entry.id)) return;
        announced.current.add(entry.id);
        shown.push(toastRef.current({ ...finishedNotice(v, entry), duration: null, isClosable: true, position: "top-right" }));
        window.dispatchEvent(new CustomEvent(IV_FINISHED_EVENT, { detail: { id: v._id || entry.id, status: v.status } }));
      } else if (v.status !== entry.status) {
        watchVerification(bid, { id: entry.id, status: v.status });
      }
    }));
    // a round ends when it is done, at its deadline, or when the notifier goes (its lock is released then)
    const bounded = () => {
      if (busy.current) return undefined;
      busy.current = true;
      const mine = ++round;
      let timer;
      const deadline = new Promise((resolve) => { timer = setTimeout(resolve, roundDeadline(pollMs)); });
      return Promise.race([tick(mine).catch(() => undefined), deadline, stopped]).finally(() => {
        clearTimeout(timer);
        // over: an answer that arrives later (its lock already released) changes nothing
        if (mine === round) {
          round += 1;
          busy.current = false;
        }
      });
    };
    // one tab at a time follows the practice's checks (where the browser can say so)
    const next = () => (typeof navigator !== "undefined" && navigator.locks?.request
      ? navigator.locks.request(`iv-notifier-${bid}`, { ifAvailable: true }, (lock) => (lock ? bounded() : undefined)).catch(() => undefined)
      : bounded());
    const t = setInterval(next, pollMs);
    return () => {
      live = false;
      round += 1;
      busy.current = false;
      stop();
      clearInterval(t);
      shown.forEach((id) => toastRef.current.close(id));
    };
  }, [bid, pollMs]);

  return null;
}
